import { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  FaEdit, FaTrash, FaEye, FaPlus, FaSearch, FaFilter,
  FaUsers, FaUserCheck, FaUserTimes, FaTimes,
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import { useApp } from '../../../context/AppContext';
import UserModal from './UserModal';
import DataTable from '../../../components_shared/DataTable';
import { userAPI } from '../../../utils/api';
import debounce from 'lodash/debounce';

const AVATAR_COLORS = [
  ['#10ac84', '#fff'], ['#0f6fad', '#fff'], ['#8e44ad', '#fff'],
  ['#e67e22', '#fff'], ['#e74c3c', '#fff'], ['#2c3e50', '#fff'],
];

function getAvatarColor(name = '') {
  const idx = (name.charCodeAt(0) || 0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #f0f4f8',
      borderRadius: 12,
      padding: '16px 20px',
      display: 'flex',
      alignItems: 'center',
      gap: 14,
      flex: 1,
      minWidth: 0,
      boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: 10,
        background: `${color}18`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color, fontSize: '1.1rem', flexShrink: 0,
      }}>
        <Icon />
      </div>
      <div>
        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: 3, fontWeight: 500 }}>{label}</div>
      </div>
    </div>
  );
}

function UserTable() {
  const { permissions, roles: userRoles } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, mode: 'create', user: null });
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalElements, setTotalElements] = useState(0);

  const isSuperAdmin = userRoles && (userRoles.includes('SUPER_ADMIN') || userRoles.includes('ROLE_SUPER_ADMIN'));

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await userAPI.getUsers({
        page,
        size: pageSize,
        search: search.trim() || undefined,
        status: statusFilter || undefined,
      });
      const content = res.data.content || [];
      const total = res.data.totalElements || 0;
      setData(content);
      setTotalElements(total);

      const activeCount = content.filter(u => (u.status || 'ACTIVE').toUpperCase() === 'ACTIVE').length;
      setStats({ total, active: activeCount, inactive: total - activeCount });
    } catch (err) {
      Swal.fire({ title: 'Error', text: err.message, icon: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, statusFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(val => { setSearch(val); setPage(0); }, 400),
    []
  );

  const handleSearchChange = e => {
    setSearchInput(e.target.value);
    debouncedSearch(e.target.value);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(0);
  };

  const handleView = user => setModal({ open: true, mode: 'view', user });
  const handleEdit = user => setModal({ open: true, mode: 'edit', user });
  const openCreate = () => setModal({ open: true, mode: 'create', user: null });

  const handleDeleteClick = async user => {
    const displayName = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username;
    const result = await Swal.fire({
      title: 'Delete User?',
      html: `Are you sure you want to delete <strong>${displayName}</strong>?<br/><span style="font-size:0.85rem;color:#94a3b8">This action cannot be undone.</span>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    setLoading(true);
    try {
      await userAPI.deleteUser(user.id);
      await fetchUsers();
      Swal.fire({ title: 'Deleted!', text: `${user.username} has been removed.`, icon: 'success', toast: true, position: 'top-end', timer: 3000, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ title: 'Error', text: err.message, icon: 'error', confirmButtonColor: '#e74c3c' });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      accessorKey: 'id',
      header: '#',
      cell: ({ getValue }) => (
        <span style={{
          display: 'inline-block', minWidth: 28, textAlign: 'center',
          background: 'rgba(15,111,173,0.08)', color: '#0f6fad',
          borderRadius: 6, padding: '2px 7px', fontSize: '0.75rem', fontWeight: 700,
        }}>
          {getValue()}
        </span>
      ),
    },
    {
      accessorKey: 'username',
      header: 'User',
      cell: ({ row }) => {
        const initial = (row.original.first_name?.[0] || row.original.username?.[0] || '?').toUpperCase();
        const [bg, fg] = getAvatarColor(row.original.username || '');
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: bg, color: fg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: '0.9rem',
            }}>
              {initial}
            </div>
            <div>
              <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{row.original.username}</div>
              <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{row.original.email || '—'}</div>
            </div>
          </div>
        );
      },
    },
    {
      header: 'Full Name',
      id: 'fullName',
      cell: ({ row }) => {
        const name = `${row.original.first_name || ''} ${row.original.last_name || ''}`.trim();
        return name || <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>—</span>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const v = (getValue() || 'ACTIVE').toUpperCase();
        const active = v === 'ACTIVE';
        return (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '4px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700,
            background: active ? 'rgba(16,172,132,0.1)' : 'rgba(231,76,60,0.08)',
            color: active ? '#10ac84' : '#e74c3c',
          }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: active ? '#10ac84' : '#e74c3c' }} />
            {v}
          </span>
        );
      },
    },
    {
      id: 'roles',
      header: 'Roles',
      cell: ({ row }) => {
        const roles = row.original.Roles || [];
        if (!roles.length) return <span style={{ color: '#cbd5e1', fontStyle: 'italic', fontSize: '0.8rem' }}>No roles</span>;
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {roles.map((r, i) => (
              <span key={i} style={{
                background: 'rgba(15,111,173,0.08)', color: '#0f6fad',
                borderRadius: 6, padding: '2px 8px', fontSize: '0.7rem', fontWeight: 600,
              }}>
                {(r.name || r).replace(/^ROLE_/, '')}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <ActionBtn icon={<FaEye />} title="View" color="#10ac84" onClick={() => handleView(row.original)} />
          {(permissions.includes('EDIT_USERS') || isSuperAdmin) && (
            <ActionBtn icon={<FaEdit />} title="Edit" color="#0f6fad" onClick={() => handleEdit(row.original)} />
          )}
          {(permissions.includes('DELETE_USERS') || isSuperAdmin) && (
            <ActionBtn icon={<FaTrash />} title="Delete" color="#e74c3c" onClick={() => handleDeleteClick(row.original)} />
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Stats bar */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <StatCard icon={FaUsers}     label="Total Users"    value={stats.total}    color="#0f6fad" />
        <StatCard icon={FaUserCheck} label="Active Users"   value={stats.active}   color="#10ac84" />
        <StatCard icon={FaUserTimes} label="Inactive Users" value={stats.inactive} color="#e74c3c" />
      </div>

      {/* Main card */}
      <div style={{
        background: '#fff',
        borderRadius: 14,
        border: '1px solid #f0f4f8',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          padding: '22px 28px 0', flexWrap: 'wrap', gap: 12,
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b' }}>User Accounts</h2>
            <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>
              Manage system access and personnel profiles
            </p>
          </div>
          {(permissions.includes('CREATE_USERS') || isSuperAdmin) && (
            <button onClick={openCreate} disabled={loading} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'linear-gradient(135deg, #10ac84, #0e9a75)',
              color: '#fff', border: 'none', borderRadius: 10,
              padding: '10px 18px', fontSize: '0.875rem', fontWeight: 600,
              cursor: 'pointer', fontFamily: "'Poppins', sans-serif",
              boxShadow: '0 2px 8px rgba(16,172,132,0.25)',
              transition: 'opacity 0.15s',
              opacity: loading ? 0.7 : 1,
            }}>
              <FaPlus /> New User
            </button>
          )}
        </div>

        {/* Filters */}
        <div style={{
          display: 'flex', gap: 10, padding: '16px 28px',
          borderBottom: '1px solid #f0f4f8', flexWrap: 'wrap', alignItems: 'center',
        }}>
          <div style={{
            flex: 1, minWidth: 200,
            display: 'flex', alignItems: 'center',
            background: '#f8fafc', border: '1.5px solid #e2e8f0',
            borderRadius: 10, padding: '0 12px', height: 40,
            transition: 'border-color 0.2s',
          }}>
            <FaSearch style={{ color: '#94a3b8', fontSize: '0.8rem', flexShrink: 0, marginRight: 8 }} />
            <input
              type="text"
              value={searchInput}
              onChange={handleSearchChange}
              placeholder="Search by name, email or username..."
              style={{
                flex: 1, border: 'none', background: 'transparent',
                fontSize: '0.875rem', color: '#374151', outline: 'none',
                fontFamily: "'Poppins', sans-serif",
              }}
            />
            {searchInput && (
              <button onClick={clearSearch} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#94a3b8', padding: 0, display: 'flex', alignItems: 'center',
              }}>
                <FaTimes style={{ fontSize: '0.75rem' }} />
              </button>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FaFilter style={{ color: '#94a3b8', fontSize: '0.75rem' }} />
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
              style={{
                padding: '8px 12px', borderRadius: 10,
                border: '1.5px solid #e2e8f0', outline: 'none',
                fontSize: '0.875rem', background: '#f8fafc', color: '#374151',
                fontFamily: "'Poppins', sans-serif", cursor: 'pointer',
              }}
            >
              <option value="">All Statuses</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div style={{ padding: '0 28px 24px' }}>
          <DataTable
            data={data}
            columns={columns}
            loading={loading}
            totalElements={totalElements}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            serverSide
          />
        </div>
      </div>

      {/* Create/Edit/View modal */}
      {modal.open && (
        <Modal onClose={() => setModal({ ...modal, open: false })} size="large">
          <UserModal mode={modal.mode} user={modal.user} onClose={() => setModal({ ...modal, open: false })} onSaved={fetchUsers} />
        </Modal>
      )}
    </div>
  );
}

function Modal({ children, onClose, size }) {
  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(15,23,42,0.6)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
        animation: 'modalFadeIn 0.18s ease',
      }}
      onClick={onClose}
    >
      <style>{`
        @keyframes modalFadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes modalPop { from { opacity:0; transform:scale(0.93) translateY(-10px) } to { opacity:1; transform:scale(1) translateY(0) } }
      `}</style>
      <div
        style={{
          background: '#fff',
          borderRadius: 18,
          width: '100%',
          maxWidth: size === 'large' ? 760 : 500,
          maxHeight: '92vh',
          overflowY: 'auto',
          boxShadow: '0 24px 80px rgba(0,0,0,0.22), 0 0 0 1px rgba(0,0,0,0.04)',
          animation: 'modalPop 0.22s cubic-bezier(0.34,1.56,0.64,1)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

function ActionBtn({ icon, title, color, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      title={title}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: 30, height: 30, borderRadius: 7,
        background: hovered ? `${color}18` : 'transparent',
        border: `1px solid ${hovered ? color : 'transparent'}`,
        color, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.8rem', transition: 'all 0.15s', padding: 0,
      }}
    >
      {icon}
    </button>
  );
}

export default UserTable;
